import './style.css';
import type { ClientMessage, Presence } from '../../shared/protocol';
import { Emulator } from './emulator';
import type { GbaRam } from './gba-ram';
import { mapName } from './firered/map-names';
import { setTildeSupport } from './firered/charset';
import { buildNewGameSave } from './firered/new-game';
import { readCamera, readPlayer } from './firered/player';
import { decodeFrontPic, MonIcons, SPECIES } from './firered/pokemon-sprites';
import { decodePlayerSprites } from './firered/sprites';
import { activeSymbols, useSymbols } from './firered/symbols';
import { GhostLayer } from './multiplayer/ghosts';
import { PresenceSender } from './multiplayer/presence';
import { downloadRom, fetchRemoteRom, type RemoteRom } from './net/rom-download';
import { GameSocket } from './net/socket';
import { identifyRom, readRomFile, RomError, sha1Hex, type RomInfo } from './rom';
import { loadFile, saveFile } from './storage';
import { confirmDialog, initTelegram, onAppVisibility } from './telegram';
import { ChatPanel } from './ui/chat';
import { mountControls } from './ui/controls';
import { DebugHud } from './ui/debug-hud';
import { NewGameScreen } from './ui/new-game-screen';
import { setupOrientation } from './ui/orientation';

const RAM_LOCATE_ATTEMPTS = 20;
const RAM_LOCATE_INTERVAL_MS = 250;
const SKIP_TITLE_TIMEOUT_MS = 40_000;
const GMAIN_VBLANK_COUNTER = 0x24; // gMain.vblankCounter2: +1 a cada quadro do GBA (~59,7/s)

const $ = <T extends HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Elemento não encontrado: ${selector}`);
  return element;
};

const ui = {
  start: $('#start'),
  game: $('#game'),
  newGame: $('#newgame'),
  booting: $('#booting'),
  greeting: $('#greeting'),
  romStatus: $('#rom-status'),
  status: $('#status'),
  playButton: $<HTMLButtonElement>('#btn-play'),
  romButton: $<HTMLButtonElement>('#btn-rom'),
  romInput: $<HTMLInputElement>('#rom-input'),
  note: $('#note'),
  canvas: $<HTMLCanvasElement>('#gba'),
  overlay: $<HTMLCanvasElement>('#overlay'),
  toasts: $('#toasts'),
  pad: $('#pad'),
  hud: $('#hud'),
  menu: $('#menu'),
  menuButton: $<HTMLButtonElement>('#btn-menu'),
  chat: $('#chat'),
  chatButton: $<HTMLButtonElement>('#btn-chat'),
};

const params = new URLSearchParams(location.search);
const telegram = initTelegram();

let emulator: Emulator | null = null;
let rom: RomInfo | null = null;
let romHeader: Uint8Array | null = null;
let ram: GbaRam | null = null;
let soundOn = true;
/** 'server' = ROM embutida (baixada do servidor); 'local' = escolhida pelo jogador. */
let romSource: 'server' | 'local' = 'local';
let access: 'ok' | 'pending' | 'outside' = 'ok';
/** A ROM em uso entrega o inicial escolhido no cadastro (só a nossa, da decompilação). */
let romGivesStarter = false;
/** Jogo criado agora neste aparelho: avisa o servidor (e a turma) quando a conexão abrir. */
let newGameToAnnounce: Extract<ClientMessage, { t: 'newgame' }> | null = null;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// ---------- Multiplayer ----------

/** Fora do Telegram (testes no navegador) entra como convidado; o servidor só aceita isso localmente. */
function guestName(): string {
  const fromUrl = params.get('nome');
  if (fromUrl) return fromUrl;
  try {
    let name = localStorage.getItem('guestName');
    if (!name) {
      name = `Treinador ${100 + Math.floor(Math.random() * 900)}`;
      localStorage.setItem('guestName', name);
    }
    return name;
  } catch {
    return 'Treinador';
  }
}

/** Credencial para o servidor: o initData assinado pelo Telegram (ou convidado, só em testes locais). */
const authorization = (): string =>
  telegram.inTelegram ? `tma ${telegram.initData}` : `guest ${encodeURIComponent(guestName())}`;

const socket = new GameSocket((): ClientMessage => {
  const room = params.get('sala') ?? undefined;
  return telegram.inTelegram
    ? { t: 'hello', initData: telegram.initData, room }
    : { t: 'hello', guestName: guestName(), room };
});
const ghosts = new GhostLayer(ui.overlay, null);
const newGameScreen = new NewGameScreen(ui.newGame);
const presence = new PresenceSender(socket);

const describePlace = (pos: Presence | null): string =>
  pos ? mapName(pos.map[0], pos.map[1]) : 'Na tela inicial';

const chat = new ChatPanel(ui.chat, ui.chatButton, ui.toasts, {
  send: (text) => socket.send({ t: 'chat', text }),
  loadMore: (before) => socket.send({ t: 'history', before }),
  describe: describePlace,
});

socket.onState((state) => chat.setConnection(state));
socket.onMessage((message) => {
  switch (message.t) {
    case 'welcome':
      ghosts.clear();
      ghosts.setSelf(message.you.id);
      for (const player of message.players) ghosts.add(player.id, player.name, player.pos);
      chat.setSelf(message.you.id, message.you.name);
      chat.welcome(message.history, message.hasMore, message.players);
      if (newGameToAnnounce && socket.send(newGameToAnnounce)) newGameToAnnounce = null;
      break;
    case 'join':
      ghosts.add(message.player.id, message.player.name, message.player.pos);
      chat.playerJoined(message.player);
      break;
    case 'leave':
      ghosts.remove(message.id);
      chat.playerLeft(message.id);
      break;
    case 'pos':
      ghosts.move(message.id, message.pos);
      chat.playerMoved(message.id, message.pos);
      break;
    case 'chat':
      chat.add(message.message);
      ghosts.say(message.message.userId, message.message.text);
      break;
    case 'history':
      chat.older(message.messages, message.hasMore);
      break;
    case 'notice':
      chat.notice(message.text);
      break;
  }
});

/** A cada quadro: lê a RAM, manda a nossa posição e desenha os outros jogadores. */
let lastPlace = '';
function frame(now: number): void {
  requestAnimationFrame(frame);
  if (!ram || !rom?.multiplayer) return;

  const player = readPlayer(ram);
  presence.tick(player, now);

  const place = player ? `${player.mapGroup}.${player.mapNum}.${player.scene}` : '';
  if (place !== lastPlace) {
    lastPlace = place;
    chat.setSelfPresence(presence.current);
    chat.refreshPlayers();
  }

  const camera = player?.scene === 'overworld' && ui.menu.hidden ? readCamera(ram) : null;
  ghosts.render(player && camera ? { player, camera } : null, now);
}

// ---------- Tela inicial e emulador ----------

function setStatus(message: string, isError = false): void {
  ui.status.textContent = message;
  ui.status.classList.toggle('error', isError);
}

function renderStart(): void {
  ui.greeting.textContent = telegram.user
    ? `Olá, ${telegram.user.first_name}!`
    : 'Aberto fora do Telegram (modo navegador).';

  if (access === 'pending') {
    ui.romStatus.innerHTML =
      '<strong>Acesso pendente</strong><br><span class="warn">Os admins já foram avisados no bot. ' +
      'Assim que liberarem, você recebe uma mensagem lá.</span>';
  } else if (rom) {
    const note = rom.multiplayer
      ? '<span class="ok">pronto para jogar com a turma</span>'
      : '<span class="warn">jogável; multiplayer ainda não validado nesta versão</span>';
    ui.romStatus.innerHTML = `<strong>${rom.label}</strong><br>${note}`;
    ui.romButton.textContent = 'Trocar ROM';
  } else if (romSource === 'server') {
    ui.romStatus.textContent = 'Preparando o jogo…';
  } else if (access === 'outside') {
    ui.romStatus.textContent = 'Abra pelo bot no Telegram para baixar o jogo (ou escolha uma ROM).';
  } else {
    ui.romStatus.textContent = 'Nenhuma ROM carregada neste aparelho.';
    ui.romButton.textContent = 'Escolher ROM';
  }
  ui.playButton.disabled = !emulator || !rom || access === 'pending';
  ui.romButton.disabled = !emulator;
  ui.romButton.hidden = romSource === 'server' || access === 'pending';
  ui.menu.querySelector<HTMLElement>('[data-action="rom"]')!.hidden = romSource === 'server';
  ui.note.textContent =
    romSource === 'server'
      ? 'O jogo é baixado uma vez e fica salvo neste aparelho.'
      : 'A ROM fica só neste aparelho e nunca é enviada para o servidor. Use o FireRed original em inglês (EUA).';
}

/** Guarda o cabeçalho e extrai os sprites dos jogadores da ROM. */
function useRom(info: RomInfo, data: Uint8Array): void {
  rom = info;
  romHeader = data.slice(0xa0, 0xb0);
  if (!info.multiplayer) return;
  try {
    const sprites = decodePlayerSprites(data);
    ghosts.setSprites(sprites);
    ghosts.setIcons(new MonIcons(data));
    newGameScreen.setSprites(sprites);
    newGameScreen.setStarters(
      romGivesStarter
        ? new Map(Object.values(SPECIES).map((species) => [species, decodeFrontPic(data, species)]))
        : null,
    );
  } catch (error) {
    console.warn('Não deu para extrair os sprites da ROM:', error);
  }
}

async function boot(): Promise<void> {
  renderStart();
  setStatus('Carregando emulador…');
  try {
    emulator = await Emulator.create(ui.canvas);
  } catch (error) {
    setStatus(`Falha ao iniciar o emulador: ${(error as Error).message}`, true);
    return;
  }

  const stored = await emulator.readStoredRom();
  const remote = await fetchRemoteRom(authorization());
  if (remote.status === 'ok') {
    romSource = 'server';
    if (stored && (await sha1Hex(stored)) === remote.rom.sha1) await useServerRom(remote.rom, stored);
    else await downloadServerRom(remote.rom);
  } else {
    if (remote.status === 'forbidden') {
      access = 'pending';
      romSource = 'server';
    } else if (remote.status === 'unauthorized' && !stored) {
      access = 'outside';
    }
    // Sem servidor (ou sem acesso a ele): usa a ROM guardada, se houver.
    if (stored && access !== 'pending') await useStoredRom(stored);
    setStatus(remote.status === 'offline' && !rom ? 'Sem conexão com o servidor.' : '', remote.status === 'offline' && !rom);
  }
  renderStart();
}

const encodeJson = (value: unknown) => new TextEncoder().encode(JSON.stringify(value));
const decodeJson = <T>(bytes: Uint8Array | null): T | null => {
  try {
    return bytes ? (JSON.parse(new TextDecoder().decode(bytes)) as T) : null;
  } catch {
    return null;
  }
};

/** ROM entregue pelo servidor: se for nossa (decompilação), traz os endereços e o til. */
async function useServerRom(meta: RemoteRom, data: Uint8Array): Promise<void> {
  if (meta.symbols) {
    useSymbols(meta.symbols);
    setTildeSupport(Boolean(meta.tilde));
    romGivesStarter = Boolean(meta.starter);
    useRom({ version: 'dynasty', label: meta.label ?? 'FireRed Dynasty', multiplayer: true, sha1: meta.sha1 }, data);
  } else {
    useSymbols(null);
    setTildeSupport(false);
    romGivesStarter = false;
    useRom(await identifyRom(data), data);
  }
  await saveFile('romMeta', encodeJson(meta));
}

/** ROM já guardada no aparelho, sem falar com o servidor (offline ou ROM escolhida à mão). */
async function useStoredRom(data: Uint8Array): Promise<void> {
  const meta = decodeJson<RemoteRom>(await loadFile('romMeta'));
  try {
    if (meta && meta.sha1 === (await sha1Hex(data))) await useServerRom(meta, data);
    else useRom(await identifyRom(data), data);
  } catch {
    // ROM desconhecida e sem dados do servidor: não dá para usar.
  }
}

/** Baixa a ROM do servidor (primeira vez ou versão nova, ex.: tradução atualizada). */
async function downloadServerRom(meta: RemoteRom): Promise<void> {
  if (!emulator) return;
  renderStart();
  try {
    const data = await downloadRom(authorization(), meta.size, (fraction) =>
      setStatus(`Baixando o jogo… ${Math.round(fraction * 100)}%`),
    );
    setStatus('Conferindo…');
    if ((await sha1Hex(data)) !== meta.sha1) throw new Error('o arquivo chegou diferente do esperado');
    await emulator.storeRom(data);
    await useServerRom(meta, data);
    setStatus('');
  } catch (error) {
    const message = error instanceof RomError ? error.message : (error as Error).message;
    setStatus(`Não deu para baixar o jogo: ${message}`, true);
  }
}

async function chooseRom(file: File): Promise<void> {
  if (!emulator) return;
  setStatus('Verificando ROM…');
  try {
    const data = await readRomFile(file);
    const info = await identifyRom(data);
    await emulator.storeRom(data);
    useRom(info, data);
    setStatus('ROM salva neste aparelho.');
  } catch (error) {
    const message = error instanceof RomError ? error.message : `Erro ao ler o arquivo: ${(error as Error).message}`;
    setStatus(message, true);
  }
  renderStart();
}

async function attachRam(): Promise<void> {
  if (!emulator || !romHeader) return;
  for (let attempt = 0; attempt < RAM_LOCATE_ATTEMPTS && !ram; attempt++) {
    ram = emulator.locateRam(romHeader);
    if (!ram) await sleep(RAM_LOCATE_INTERVAL_MS);
  }
  if (!ram) console.warn('RAM do GBA não encontrada na memória do WASM.');
}

async function play(): Promise<void> {
  if (!emulator || !rom) return;
  ui.start.hidden = true;

  // Jogador novo neste aparelho: nossa tela no lugar da conversa do Prof. Carvalho.
  const isNewPlayer = !(await emulator.hasProgress());
  // Sem "continuar de onde parou" (jogador novo ou ROM atualizada): pula a tela título.
  const skipTitle = isNewPlayer || !(await emulator.hasState());
  if (isNewPlayer) {
    const choice = await newGameScreen.ask(telegram.user?.first_name ?? guestName());
    const template = new Uint8Array(await (await fetch('novo-jogo.sav')).arrayBuffer());
    const [trainerId] = crypto.getRandomValues(new Uint32Array(1));
    await emulator.installSave(buildNewGameSave(template, { ...choice, trainerId }));
    newGameToAnnounce = { t: 'newgame', nick: choice.name, starter: choice.starter };
  }
  ui.booting.hidden = !skipTitle;

  ui.game.hidden = false;
  try {
    await emulator.start();
  } catch (error) {
    ui.game.hidden = true;
    ui.booting.hidden = true;
    ui.start.hidden = false;
    setStatus((error as Error).message, true);
    return;
  }
  socket.connect();
  await emulator.resumeAudio();
  await attachRam();
  if (skipTitle) {
    await skipTitleScreen();
    await emulator.persistNow();
  }
}

/**
 * Do save novo até o quarto: passa a abertura, a tela título e o CONTINUAR apertando
 * A em velocidade acelerada, por trás da cortina "Preparando sua aventura…".
 */
async function skipTitleScreen(): Promise<void> {
  if (!emulator) return;
  const tap = async (button: 'A' | 'B', gap: number) => {
    emulator!.press(button);
    await sleep(80);
    emulator!.release(button);
    await sleep(gap);
  };
  await ensureSpeed(true);
  const deadline = performance.now() + SKIP_TITLE_TIMEOUT_MS;
  while (performance.now() < deadline && !(ram && readPlayer(ram)?.scene === 'overworld')) await tap('A', 120);
  await ensureSpeed(false);
  // Ao continuar, o jogo pode mostrar o resumo "Da última vez…": B pula.
  for (let i = 0; i < 3; i++) await tap('B', 250);
  ui.booting.hidden = true;
}

/** Quadros do GBA por segundo, medidos no contador do próprio jogo. */
async function gameFps(ms = 300): Promise<number> {
  if (!ram) return 0;
  const counter = activeSymbols().gMain + GMAIN_VBLANK_COUNTER;
  const before = ram.u32(counter);
  const start = performance.now();
  await sleep(ms);
  return ((ram.u32(counter) - before) * 1000) / (performance.now() - start);
}

/** Liga/desliga o avanço rápido e confere de verdade (o comando do RetroArch só alterna). */
async function ensureSpeed(fast: boolean): Promise<void> {
  if (!emulator || !ram) return;
  for (let attempt = 0; attempt < 6; attempt++) {
    const fps = await gameFps();
    const isFast = fps > 100;
    if (isFast === fast) return;
    emulator.toggleFastForward();
    await sleep(150);
  }
  console.warn(`Não consegui ${fast ? 'ligar' : 'desligar'} o avanço rápido.`);
}

function hudLines(): string[] {
  const lines = [
    `telegram: ${telegram.inTelegram ? `${telegram.platform} v${telegram.version}` : 'não'}`,
    `rede: ${socket.state}`,
    `ram: ${ram ? `ok (ewram @ 0x${ram.ewramBase.toString(16)})` : 'procurando…'}`,
  ];
  if (!ram || !rom?.multiplayer) return lines;

  const player = readPlayer(ram);
  if (!player) return [...lines, 'jogador: fora do mapa'];
  return [
    ...lines,
    `mapa: ${player.mapGroup}.${player.mapNum} ${mapName(player.mapGroup, player.mapNum)}`,
    `pos: ${player.x},${player.y} ${player.facing}${player.moving ? (player.fast ? ' (correndo)' : ' (andando)') : ''}`,
    `cena: ${player.scene}`,
  ];
}

function setupMenu(hud: DebugHud): void {
  const soundButton = ui.menu.querySelector<HTMLButtonElement>('[data-action="sound"]')!;
  const close = () => {
    ui.menu.hidden = true;
    emulator?.resume();
  };

  ui.menuButton.addEventListener('click', () => {
    emulator?.pause();
    ui.menu.hidden = false;
  });

  ui.menu.addEventListener('click', async (event) => {
    const action = (event.target as HTMLElement).closest<HTMLElement>('[data-action]')?.dataset.action;
    switch (action) {
      case 'hud':
        hud.toggle();
        close();
        break;
      case 'sound':
        soundOn = !soundOn;
        emulator?.setMuted(!soundOn);
        soundButton.textContent = `Som: ${soundOn ? 'ligado' : 'desligado'}`;
        break;
      case 'reset':
        if (!(await confirmDialog('Apagar o progresso deste aparelho e começar um jogo novo?'))) break;
        await emulator?.resetProgress();
        location.reload();
        break;
      case 'rom':
        // Recarrega a página para trocar de ROM com o emulador limpo.
        await emulator?.persistNow();
        await emulator?.forgetRom();
        location.reload();
        break;
      case 'close':
        close();
        break;
    }
  });
}

ui.romButton.addEventListener('click', () => ui.romInput.click());
ui.romInput.addEventListener('change', () => {
  const file = ui.romInput.files?.[0];
  ui.romInput.value = '';
  if (file) void chooseRom(file);
});
ui.playButton.addEventListener('click', () => void play());

onAppVisibility((visible) => {
  if (!emulator?.isRunning) return;
  if (visible) {
    if (ui.menu.hidden) emulator.resume();
    socket.reconnectNow();
  } else {
    emulator.pause();
    void emulator.persistNow();
  }
});
window.addEventListener('pagehide', () => void emulator?.persistNow());

// Acesso pelo console do navegador durante o desenvolvimento.
if (import.meta.env.DEV) {
  Object.assign(window, {
    __game: {
      get emulator() {
        return emulator;
      },
      get ram() {
        return ram;
      },
      readPlayer: () => (ram ? readPlayer(ram) : null),
      readCamera: () => (ram ? readCamera(ram) : null),
      socket,
      ghosts,
      chat,
    },
  });
}

setupOrientation(ui.game);
const hud = new DebugHud(ui.hud, hudLines);
setupMenu(hud);
requestAnimationFrame(frame);
void boot().then(() => {
  if (emulator) mountControls(ui.pad, emulator);
  if (params.has('debug')) hud.toggle(true);
});
